import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import styled, { keyframes } from "styled-components";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";

// Window after the last action before the toast auto-closes and the
// accumulated IDs are committed (no longer undoable inline).
const UNDO_WINDOW_MS = 10_000;

/**
 * Rolling undo for status-change actions (dismiss, contacted). Bundles
 * consecutive actions into a single persistent toast that grows — rather
 * than spamming N toasts when the rep rapidly dismisses multiple rows or
 * contacts several projects. The timer resets on each new action, so the
 * rep effectively has UNDO_WINDOW_MS after their *last* action to change
 * their mind.
 *
 * Callers invoke `record(ids)` after a successful action; `onUndo(ids)`
 * fires when the rep clicks Undo (batches all accumulated IDs).
 *
 * `verb` is the past-tense label shown in the toast ("dismissed",
 * "contacted"). `countBy` controls what the displayed number represents:
 *   - "row" (default): count unique issue IDs — right for row-scoped
 *     actions like dismiss, where "4 dismissed" means 4 rows.
 *   - "call": count record() calls — right for project-scoped actions
 *     like Mark Contacted, where one click covers N rows but is one
 *     unit of rep intent ("2 contacted" = 2 projects).
 */
export function useStatusUndo({
  onUndo,
  verb = "dismissed",
  countBy = "row",
  unitLabel,
}) {
  const bundleRef = useRef({
    ids: new Set(),
    calls: 0,
    toastId: null,
    timer: null,
  });

  useEffect(
    () => () => {
      const b = bundleRef.current;
      if (b.timer) clearTimeout(b.timer);
      if (b.toastId) toast.dismiss(b.toastId);
    },
    [],
  );

  const reset = () => {
    bundleRef.current = {
      ids: new Set(),
      calls: 0,
      toastId: null,
      timer: null,
    };
  };

  const undoNow = useCallback(async () => {
    const b = bundleRef.current;
    const ids = Array.from(b.ids);
    if (b.timer) clearTimeout(b.timer);
    if (b.toastId) toast.dismiss(b.toastId);
    reset();
    if (ids.length === 0) return;
    try {
      await onUndo(ids);
      toast.success(
        `Restored ${ids.length} issue${ids.length === 1 ? "" : "s"}`,
        { autoClose: 2500 },
      );
    } catch (e) {
      toast.error(`Undo failed: ${e.message || e}`);
    }
  }, [onUndo]);

  const record = useCallback(
    (newIds) => {
      if (!newIds || newIds.length === 0) return;

      const b = bundleRef.current;
      if (b.timer) clearTimeout(b.timer);

      for (const id of newIds) b.ids.add(id);
      b.calls += 1;
      const count = countBy === "call" ? b.calls : b.ids.size;
      // `nonce` changes on every record() so the countdown's `key` changes
      // and the animation restarts. Without it, toast.update keeps the same
      // DOM element and the CSS animation would play through once and stop.
      const nonce = Date.now();
      const content = (
        <UndoToastBody
          count={count}
          verb={verb}
          unitLabel={unitLabel}
          onUndo={undoNow}
          nonce={nonce}
        />
      );

      if (b.toastId && toast.isActive(b.toastId)) {
        toast.update(b.toastId, { render: content });
      } else {
        b.toastId = toast(content, {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          hideProgressBar: true,
          closeButton: false,
          position: "bottom-right",
          className: "status-undo-toast",
        });
      }

      b.timer = setTimeout(() => {
        if (bundleRef.current.toastId) {
          toast.dismiss(bundleRef.current.toastId);
        }
        reset();
      }, UNDO_WINDOW_MS);
    },
    [undoNow, verb, countBy, unitLabel],
  );

  return { record };
}

function UndoToastBody({ count, verb, unitLabel, onUndo, nonce }) {
  const countColor =
    verb === "dismissed" ? COLORS.alertRed : COLORS.stormtechOrange;
  const labelText = unitLabel
    ? `${unitLabel}${count === 1 ? "" : "s"} ${verb}`
    : verb;
  return (
    <ToastRow>
      <Count $color={countColor}>{count}</Count>
      <Label>{labelText}</Label>
      <UndoBtn type="button" onClick={onUndo}>
        Undo
      </UndoBtn>
      <Countdown key={nonce} />
    </ToastRow>
  );
}

const ToastRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 2px 6px 8px;
  font-family: inherit;
`;

const shrinkBar = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

const Countdown = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: ${COLORS.stormtechOrange};
  transform-origin: left center;
  animation: ${shrinkBar} ${UNDO_WINDOW_MS}ms linear forwards;
`;

const Count = styled.span`
  font-size: 22px;
  font-weight: ${FONTWEIGHT.heavy};
  color: ${({ $color }) => $color};
  line-height: 1;
  min-width: 20px;
  text-align: center;
`;

const Label = styled.span`
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${COLORS.text};
  flex: 1;
`;

const UndoBtn = styled.button`
  background: ${COLORS.stormtechOrange};
  color: ${COLORS.primaryInverted};
  border: 0;
  border-radius: 4px;
  padding: 6px 16px;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.08);
  }

  &:active {
    filter: brightness(0.95);
  }
`;
